import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';
import axios from 'axios';

const UserManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Fetch all customers on component mount
  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/customers`);
      setCustomers(response.data);
      setFilteredCustomers(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Failed to fetch customers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter customers based on search term and role filter
  useEffect(() => {
    let filtered = [...customers];
    
    // Apply role filter
    if (roleFilter !== 'all') {
      const adminLevel = parseInt(roleFilter);
      filtered = filtered.filter(customer => 
        (customer.is_admin === adminLevel) || 
        (adminLevel === 0 && (customer.is_admin === undefined || customer.is_admin === null))
      );
    }
    
    // Apply search term filter
    if (searchTerm.trim()) {
      const searchTermLower = searchTerm.toLowerCase();
      
      // For phone numbers, strip all non-digit characters for comparison
      const normalizedSearchTerm = searchTermLower.replace(/[^0-9]/g, '');
      
      filtered = filtered.filter(customer => {
        // Name and email search (case insensitive)
        const nameMatch = customer.name && customer.name.toLowerCase().includes(searchTermLower);
        const emailMatch = customer.email_address && customer.email_address.toLowerCase().includes(searchTermLower);
        
        // Phone search with normalization for both search term and stored phone
        let phoneMatch = false;
        if (customer.phone && normalizedSearchTerm.length > 0) {
          const normalizedPhone = customer.phone.replace(/[^0-9]/g, '');
          phoneMatch = normalizedPhone.includes(normalizedSearchTerm);
        }
        
        return nameMatch || emailMatch || phoneMatch;
      });
    }
    
    setFilteredCustomers(filtered);
  }, [searchTerm, roleFilter, customers]);

  const updateCustomerAdminLevel = async (customerId, newAdminLevel) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/customer/update-admin-level`, {
        customerId,
        adminLevel: newAdminLevel
      });
      
      if (response.data.success) {
        setCustomers(prevCustomers => 
          prevCustomers.map(customer => 
            customer.id === customerId 
              ? { ...customer, is_admin: newAdminLevel } 
              : customer
          )
        );
        setSuccess(`Successfully updated admin level for customer ${customerId}`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error(response.data.error || 'Failed to update admin level');
      }
    } catch (err) {
      console.error('Error updating admin level:', err);
      setError(`Failed to update admin level: ${err.response?.data?.error || err.message}`);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const getAdminLevelLabel = (level) => {
    switch (Number(level)) {
      case 0:
        return 'Regular User';
      case 1:
        return 'Admin Level 1';
      case 2:
        return 'Admin Level 2';
      default:
        return `Unknown (${level})`;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">User Management</h1>
      
      <div className="mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-grow">
            <input
              type="text"
              placeholder="Search by name, phone, or email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="p-2 border rounded text-black w-full"
            />
            <div className="mt-1 text-xs text-gray-400">
              Phone search: digits only or formatted (555-123-4567, (555) 123-4567)
            </div>
          </div>
          <div className="flex gap-4">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="p-2 border rounded text-black"
            >
              <option value="all">All Roles</option>
              <option value="0">Regular Users</option>
              <option value="1">Admin Level 1</option>
              <option value="2">Admin Level 2</option>
            </select>
            <button 
              onClick={fetchCustomers}
              className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 bg-gray-100 border-b">
          <div className="flex justify-between items-center">
            <div>
              <span className="font-semibold text-gray-700">
                {filteredCustomers.length} {filteredCustomers.length === 1 ? 'customer' : 'customers'} found
              </span>
              {roleFilter !== 'all' && (
                <span className="ml-2 text-sm text-gray-500">
                  (filtered by {getAdminLevelLabel(parseInt(roleFilter))})
                </span>
              )}
            </div>
          </div>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact Info
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Admin Level
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading && filteredCustomers.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                  Loading customers...
                </td>
              </tr>
            ) : filteredCustomers.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                  No customers found
                </td>
              </tr>
            ) : (
              filteredCustomers.map(customer => (
                <tr key={customer.id} className="text-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {customer.name || 'N/A'}
                  </td>
                  <td className="px-6 py-4">
                    <div>{customer.phone || 'No phone'}</div>
                    <div className="text-gray-500 text-sm">{customer.email_address || 'No email'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getAdminLevelLabel(customer.is_admin)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <select 
                        value={customer.is_admin || 0}
                        onChange={(e) => updateCustomerAdminLevel(customer.id, Number(e.target.value))}
                        className="p-1 border rounded text-black"
                        disabled={loading}
                      >
                        <option value={0}>Regular User</option>
                        <option value={1}>Admin Level 1</option>
                        <option value={2}>Admin Level 2</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserManagement;